import { useEffect, useRef, useState } from "react";
import { lenisRef } from "@/lib/nav";

export const useCompactRosary = () => {
  const [compact, setCompact] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [actionsHeight, setActionsHeight] = useState(0);
  const anchorRef = useRef(null);
  const initiallyAligned = useRef(false);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const update = () => setCompact(media.matches);
    media.addEventListener("change", update);
    // Observe the bar, not its expanding mobile navigation panel.
    const bar = document.querySelector('[data-testid="main-header"] > div');
    const actions = document.querySelector('[data-testid="rosary-large-actions"]');
    const measure = () => { setHeaderHeight(Math.ceil(bar?.getBoundingClientRect().height || 0)); setActionsHeight(Math.ceil(actions?.getBoundingClientRect().height || 0)); };
    // React layout updates must not run inside ResizeObserver's delivery cycle.
    let measureFrame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(measureFrame);
      measureFrame = requestAnimationFrame(measure);
    });
    if (bar) observer.observe(bar);
    if (actions) observer.observe(actions);
    measure();
    return () => { media.removeEventListener("change", update); observer.disconnect(); cancelAnimationFrame(measureFrame); };
  }, []);
  useEffect(() => {
    if (!compact || !headerHeight || window.location.hash !== "#pray") return;
    let frame;
    let textSize = document.documentElement.dataset.textSize;
    const visibleReader = () => {
      const box = document.querySelector('[data-testid="mobile-rosary-reader"]')?.getBoundingClientRect();
      return box && box.top < window.innerHeight && box.bottom > headerHeight;
    };
    const capture = () => {
      // Do not pull someone back from the Scapular section or move a modal's backdrop.
      if (!visibleReader() || document.querySelector('[data-testid="rosary-fullscreen-modal"]')) { anchorRef.current = null; return; }
      const reader = document.getElementById("pray");
      const bar = document.querySelector('[data-testid="main-header"] > div');
      anchorRef.current = { offset: reader.getBoundingClientRect().top - bar.getBoundingClientRect().height };
    };
    const alignAfterLayout = () => {
      if (!anchorRef.current) return;
      cancelAnimationFrame(frame);
      let previousTop = null;
      let stableFrames = 0;
      let attempts = 0;
      const settleUntil = performance.now() + 600;
      const settle = () => {
        const reader = document.getElementById("pray");
        if (!reader || !anchorRef.current || window.location.hash !== "#pray") return;
        const rect = reader.getBoundingClientRect();
        const documentTop = rect.top + window.scrollY;
        stableFrames = previousTop !== null && Math.abs(documentTop - previousTop) < 0.5 ? stableFrames + 1 : 0;
        previousTop = documentTop;
        attempts += 1;
        const bar = document.querySelector('[data-testid="main-header"] > div');
        const desiredTop = (bar?.getBoundingClientRect().height || headerHeight) + anchorRef.current.offset;
        if ((stableFrames >= 3 || attempts >= 12 || performance.now() >= settleUntil) && Math.abs(rect.top - desiredTop) > 1) {
          // Use the actual document position after font reflow. Lenis's animated
          // position may lag behind WebKit's native scroll anchoring here.
          const target = Math.max(0, documentTop - desiredTop);
          if (lenisRef.current) {
            // Font resizing changes document height; refresh Lenis's cached
            // scroll limit before it can clamp the new target to the old page.
            lenisRef.current.resize();
            lenisRef.current.scrollTo(target, { immediate: true, force: true });
          }
          else window.scrollTo({ top: target, behavior: "instant" });
        }
        // WebKit can perform a second reflow after the first stable frames.
        // Briefly verify alignment, but never fight a user's touch or scroll.
        if (performance.now() < settleUntil) frame = requestAnimationFrame(settle);
        else anchorRef.current = null;
      };
      frame = requestAnimationFrame(settle);
    };
    if (!initiallyAligned.current) { initiallyAligned.current = true; anchorRef.current = { offset: 0 }; }
    alignAfterLayout();
    window.addEventListener("theotokos:before-text-size", capture);
    const observer = new MutationObserver(() => {
      const next = document.documentElement.dataset.textSize;
      if (next !== textSize) { textSize = next; alignAfterLayout(); }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-text-size"] });
    const cancel = () => { cancelAnimationFrame(frame); anchorRef.current = null; };
    window.addEventListener("pointerdown", cancel, { passive: true });
    window.addEventListener("wheel", cancel, { passive: true });
    return () => { cancelAnimationFrame(frame); observer.disconnect(); window.removeEventListener("theotokos:before-text-size", capture); window.removeEventListener("pointerdown", cancel); window.removeEventListener("wheel", cancel); };
  }, [compact, headerHeight]);
  return { compact, headerHeight, actionsHeight };
};